/**
 * Trees as dots on a canvas, in a local metric projection (north up, metres square). Cheaper
 * than a second WebGL map for thumbnails; `interactive` adds wheel zoom and drag pan.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CHART } from '@/theme/colors';
import type { Position } from 'geojson';
import { localProjection, type XY } from './geometry';
import { extent } from './stats';
import type { Tree } from './types';

interface Projected {
  /** x, y of tree i at 2i, 2i + 1, in metres of the local projection. */
  xy: Float64Array;
  project: (p: Position) => XY;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

// Every map of one stand shares one projection of its trees (a tab shows six maps of them).
const projections = new WeakMap<Tree[], Projected>();

function projectedOf(trees: Tree[]): Projected {
  const hit = projections.get(trees);
  if (hit) return hit;
  const lon0 = trees.reduce((s, t) => s + t.lon, 0) / trees.length;
  const lat0 = trees.reduce((s, t) => s + t.lat, 0) / trees.length;
  const project = localProjection(lon0, lat0);
  const xy = new Float64Array(trees.length * 2);
  trees.forEach((t, i) => xy.set(project([t.lon, t.lat]), 2 * i));
  const xs = xy.filter((_, i) => i % 2 === 0);
  const ys = xy.filter((_, i) => i % 2 === 1);
  const [minX, maxX] = extent(Array.from(xs));
  const [minY, maxY] = extent(Array.from(ys));
  const projected = { xy, project, minX, maxX, minY, maxY };
  projections.set(trees, projected);
  return projected;
}

/** Where projected metres land in a `width` × `height` box, padded, aspect kept, north up. */
function frameOf(p: Projected, width: number, height: number, pad: number): (x: number, y: number) => [number, number] {
  const spanX = p.maxX - p.minX;
  const spanY = p.maxY - p.minY;
  const scale = spanX || spanY ? Math.min((width - 2 * pad) / (spanX || Infinity), (height - 2 * pad) / (spanY || Infinity)) : 1;
  const offX = (width - spanX * scale) / 2;
  const offY = (height - spanY * scale) / 2;
  return (x, y) => [offX + (x - p.minX) * scale, height - (offY + (y - p.minY) * scale)];
}

export function fitFrame(trees: Tree[], width: number, height: number, pad = 8): (t: Tree) => [number, number] {
  if (!trees.length) return () => [width / 2, height / 2];
  const p = projectedOf(trees);
  const frame = frameOf(p, width, height, pad);
  return (t) => frame(...p.project([t.lon, t.lat]));
}

/**
 * Most dots a still map draws: past this they overlap on its few hundred pixels anyway, so a
 * large stand is drawn as every n-th tree. A zoomable map draws them all.
 */
export const MAX_DOTS = 10_000;

export interface DotMapProps {
  trees: Tree[];
  /** A tree's colour, or null to draw it muted underneath the coloured ones. */
  colorOf: (t: Tree) => string | null;
  width?: number;
  height?: number;
  radius?: number;
  interactive?: boolean;
  label: string;
}

const MAX_ZOOM = 12;

export function DotMap({ trees, colorOf, width = 600, height = 360, radius = 2, interactive = false, label }: DotMapProps) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [view, setView] = useState({ scale: 1, dx: 0, dy: 0 });
  const drag = useRef<{ x: number; y: number } | null>(null);
  const frame = useMemo(() => (trees.length ? frameOf(projectedOf(trees), width, height, 8) : null), [trees, width, height]);

  // Before paint, so a map mounted for printing is drawn before the page is captured.
  useLayoutEffect(() => {
    const ctx = ref.current?.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, width, height);
    if (!frame) return;
    const { xy } = projectedOf(trees);
    // One path per colour: a fill per dot is what made a large stand slow.
    const muted: number[] = [];
    const byColour = new Map<string, number[]>();
    const count = interactive ? trees.length : Math.min(trees.length, MAX_DOTS);
    for (let k = 0; k < count; k++) {
      const i = count === trees.length ? k : Math.floor((k * trees.length) / count);
      const c = colorOf(trees[i]!);
      if (c === null) muted.push(i);
      else if (byColour.has(c)) byColour.get(c)!.push(i);
      else byColour.set(c, [i]);
    }
    ctx.save();
    ctx.translate(view.dx, view.dy);
    ctx.scale(view.scale, view.scale);
    const r = radius / Math.sqrt(view.scale);
    const fill = (colour: string, alpha: number, dots: number[]) => {
      ctx.globalAlpha = alpha;
      ctx.fillStyle = colour;
      ctx.beginPath();
      for (const i of dots) {
        const [x, y] = frame(xy[2 * i]!, xy[2 * i + 1]!);
        ctx.moveTo(x + r, y);
        ctx.arc(x, y, r, 0, 2 * Math.PI);
      }
      ctx.fill();
    };
    // The muted ones underneath the coloured ones.
    if (muted.length) fill(CHART.dotMuted, 0.12, muted);
    for (const [colour, dots] of byColour) fill(colour, 0.9, dots);
    ctx.restore();
  }, [trees, colorOf, frame, view, width, height, radius, interactive]);

  useEffect(() => {
    const canvas = ref.current;
    if (!interactive || !canvas) return;
    // Native and non-passive: React's onWheel is passive and could not stop the page scrolling.
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const box = canvas.getBoundingClientRect();
      const px = ((e.clientX - box.left) / box.width) * width;
      const py = ((e.clientY - box.top) / box.height) * height;
      setView((v) => {
        const scale = Math.min(MAX_ZOOM, Math.max(1, v.scale * (e.deltaY < 0 ? 1.2 : 1 / 1.2)));
        const k = scale / v.scale;
        return { scale, dx: px - (px - v.dx) * k, dy: py - (py - v.dy) * k };
      });
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [interactive, width, height]);

  const handlers = interactive
    ? {
        onPointerDown: (e: React.PointerEvent) => {
          drag.current = { x: e.clientX, y: e.clientY };
        },
        onPointerMove: (e: React.PointerEvent) => {
          if (!drag.current) return;
          const box = ref.current!.getBoundingClientRect();
          const dx = ((e.clientX - drag.current.x) / box.width) * width;
          const dy = ((e.clientY - drag.current.y) / box.height) * height;
          drag.current = { x: e.clientX, y: e.clientY };
          setView((v) => ({ ...v, dx: v.dx + dx, dy: v.dy + dy }));
        },
        onPointerUp: () => {
          drag.current = null;
        },
        onPointerLeave: () => {
          drag.current = null;
        },
      }
    : {};

  return (
    <canvas
      ref={ref}
      width={width}
      height={height}
      role="img"
      aria-label={label}
      style={{ width: '100%', height: 'auto', display: 'block', cursor: interactive ? 'grab' : undefined, touchAction: interactive ? 'none' : undefined }}
      {...handlers}
    />
  );
}
